<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
import { useColor } from '@/composables/color';
import ElementLibrarySelector from '@/components/DesignStudio/ElementLibrarySelector.vue';
import ColorOptionsClipboardButtons from '@/components/DesignStudio/ColorOptionsClipboardButtons.vue';
import CButton from '@/components/shared/CButton';
import CSlider from '@/components/shared/CSlider.vue';
import CInput from '@/components/shared/CInput';
import CFileInput from '@/components/shared/CFileInput';
import CSelect, { type Primitive, type CSelectItem, valueReducer } from '@/components/shared/CSelect';
import type { CanvasEx } from '@/typings/canvas';
import type {
    ActiveSelection,
    BaseTextElement,
    BoundedTextElement,
    FontColorOption,
    PathITextElement,
    TextTexture,
    TextTransformElement
} from '@/typings/customilyElements/canvasElements';
import type { ColorObject, GradientObject, LibraryDetailElement, PaletteObject } from '@/typings/libraries';
import type { ElementMapOption } from './types';
import type { KeyValueDto } from '@/typings/keyValueDto';
import type { UserTextTexture } from '@/api/typings/product';
import { LibraryTypeEnum } from '@/utils/enums/libraryType.enum';
import { getLibraryElementDetails } from '@/api/libraries';
import { runTasksInChunks } from '@/utils/promiseUtils';
import { useColorOptionsClipboard } from '@/composables/useColorOptionsClipboard';
import { useToaster } from '@/composables/toaster';
import { DatadogEvent, fireDatadogLog } from '@/utils/datadogUtils';
import { recordFeatureUsage, TrackedFeature } from '@/utils/featureUsage';

const DEFAULT_STROKE_COLOR = '#000000';
const INITIAL_STROKE_COLOR = '#FF0054';
const KEY_MAP = {
  outline: {
    strokeOption: 'outlineColorOption',
    strokeColorsMap: 'outlineColorsMap',
    strokeLibraryId: 'outlineColorLibraryId',
    strokeColor: 'outlineColor',
    type: 'outline',
  },
  stroke: {
    strokeOption: 'strokeColorOption',
    strokeColorsMap: 'strokeColorsMap',
    strokeLibraryId: 'strokeColorLibraryId',
    strokeColor: 'strokeColor',
    type: 'stroke',
  },
} as const
const COLOR_TYPE = LibraryTypeEnum.Color;
type TextElement = BoundedTextElement | TextTransformElement | PathITextElement;
type COLOR_MAP = 'fontColorsMap' | 'outlineColorsMap' | 'strokeColorsMap';
type LIBRARY_MAP = 'colorLibraryId' | 'outlineColorLibraryId' | 'strokeColorLibraryId';

const props = withDefaults(
    defineProps<{
        canvas: CanvasEx;
        textElement?: TextElement;
        editMode?: boolean;
        isTextureDisabled?: boolean;
        isGradientDisabled?: boolean;
        textureList?: UserTextTexture[];
        cmykMode?: boolean;
        forTypes?: string[];
        allowTextureUpload?: boolean;
        selectedGroup?: ActiveSelection<BaseTextElement>
        shadowApplied?: boolean;
    }>(),
    {
        allowTextureUpload: true,
        textureList: () => []
    }
);

const emit = defineEmits<{
    (e: 'open-color-library-selector'): void;
    (e: 'open-outline-color-library-selector'): void;
    (e: 'open-stroke-color-library-selector'): void;
    (e: 'texture-selected', path?: string): void;
    (e: 'texture-upload', file: File): void;
    (e: 'texture-delete', path: string): void;
    (e: 'element-props-updated', value: string): void;
}>();

const type = ref({
    text: true,
    warpText: false,
    circularText: false,
    iText: false
});

/** Warped text stores its stroke under `stroke*` keys, every other text type under `outline*`. */
const outlineKeyMap = computed(() => type.value.warpText ? KEY_MAP.stroke : KEY_MAP.outline);

const showMenuTypes = computed(() => {
    // This is a temporary solution to disable gradients for text elements
    // until we have a proper design for gradients in text elements.
    if (props.isGradientDisabled) return false;
    if (type.value.circularText || type.value.iText) return false;
    return true;
});

const isPaletteEnabled = computed(() => type.value.text);
const isStrokeEnabled = ref(false);
const isBoundedText = computed(() => props.textElement?.type === 'bounded-text');
const hasTextPathDraw = computed(
    () => isBoundedText.value && !!(props.textElement as BoundedTextElement)?.textPath
);

const disabled = ref(false);
const model = reactive({
    /* outline */
    outlineWidth: 0 as number | undefined,
    outlineColorOption: undefined as number | undefined,
    outlineColorsMap: new Map<number, FontColorOption>(),
    outlineColorLibraryId: undefined as number | undefined,
    /* fill */
    colorLibraryId: undefined as number | undefined,
    fontColorOption: undefined as number | undefined,
    fontColorsMap: new Map<number, FontColorOption>(),
    textures: [] as TextTexture[],
    texture: '',
    textureScale: 0,
    isFontColorsMixed: false,
    isColorLibraryIdMixed: false,
    isOutlineColorsMixed: false,
    isOutlineColorLibraryIdMixed: false,
});

const colord = useColor();

const transformHexToColorObj = (color: string): ColorObject => {
    const { cmyk, hex, a } = colord.getColorFromString(color);
    return {
        hex,
        alpha: a,
        cmyk: colord.cmykToDecimalString(cmyk)
    };
};

const transformFontColorMap = (element: BaseTextElement) => {
    const color = model.fontColorsMap.get(1);

    if (typeof color != 'string') return;

    if (!colord.isValidColor(color)) {
        model.fontColorsMap = new Map();
        return;
    }

    const newMap = new Map<number, FontColorOption>();

    model.fontColorsMap.forEach((value, key) => {
        if (typeof value == 'string') {
            newMap.set(key, transformHexToColorObj(value));
            return
        };
        newMap.set(key, value);
    })

    element.fontColorsMap = newMap;
    model.fontColorsMap = new Map(element.fontColorsMap);
};

const resolveType = (element: TextElement) => {
    type.value = {
        text: false,
        circularText: false,
        warpText: false,
        iText: false
    };

    switch (element.type) {
        case 'bounded-text':
            type.value.text = true;
            if (element.Itext) type.value.iText = true;
            break;
        case 'path-itext':
            type.value.circularText = true;
            break;
        case 'text-transform':
            type.value.warpText = true;
            break;
    }
};

// Legacy curved texts carry the outline as a bare hex/rgba string with no color map behind it
const seedCircularOutlineColor = (element: TextElement) => {
    if (element.type != 'path-itext') return;
    if (element.outlineColorsMap.size || element.outlineColorLibraryId) return;
    element.outlineColorsMap.set(1, transformHexToColorObj(element.outlineColor));
};

watch(
    () => props.textElement,
    (element) => {
        if (!element || (props.forTypes?.length && !props.forTypes.includes(element.type))) return;

        resolveType(element);

        disabled.value = element.locked;

        model.fontColorOption = element.fontColorOption;
        model.colorLibraryId = element.colorLibraryId;
        model.fontColorsMap = new Map(element.fontColorsMap);

        /**
         * For old templates or new templates cloned from the old ones,
         * the font color could be saved as a hex string instead of the standard color object
         */
        if (typeof element.color == 'string' && colord.isValidColor(element.color)) {
            element.color = transformHexToColorObj(element.color);
            transformFontColorMap(element as BaseTextElement);
        }

        model.outlineWidth = element.getOutlineWidth?.() || 0;
        model.outlineColorOption = element.outlineColorOption;
        model.outlineColorLibraryId = element.outlineColorLibraryId;
        model.outlineColorsMap = new Map(element.outlineColorsMap);

        switch (element.type) {
            case 'bounded-text':
                model.textures = element.textures || [];
                model.texture = element.texture;
                model.textureScale = element.textureScale == undefined ? 100 : element.textureScale;
                break;
            case 'path-itext':
                model.outlineWidth = element.strokeWidth;
                seedCircularOutlineColor(element);
                model.outlineColorsMap = new Map(element.outlineColorsMap);
                break;
            case 'text-transform':
                // Initialize stroke properties
                model.outlineWidth = element.getStrokeWidth() || 0;
                model.outlineColorOption = element.strokeColorOption;
                model.outlineColorLibraryId = element.strokeColorLibraryId;
                model.outlineColorsMap = new Map(element.strokeColorsMap);
                break;
        }
        isStrokeEnabled.value = !!model.outlineWidth;
    },
    { immediate: true }
);

const isColorMapMixed = (
    firstMap: BaseTextElement['fontColorsMap'],
    secondMap: BaseTextElement['fontColorsMap']
): boolean => {
    if (firstMap.size !== secondMap.size) return true;
    return [...firstMap.entries()].some(([key, value]) => {
        const mapValue = secondMap.get(key);
        return mapValue && normalizeColorMap(value) !== normalizeColorMap(mapValue);
    });
};

const normalizeColorMap = (color: FontColorOption) => {
    if ('hex' in color)
        return `hex:${color.hex}`;

    if ('angle' in color && 'type' in color && Array.isArray(color.colorStops)) {
        const stops = color.colorStops
            .map((cs) => cs.hex)
            .sort()
            .join(',');
        const offset = color.colorStops
            .map((cs) => cs.offset)
            .sort()
            .join(',');

        return `gradient:angle=${color.angle};type=${color.type};stops=${stops};offsets=${offset}`;
    }

    throw new Error('Object not recognized');
};

const getElementStroke = (element: BaseTextElement): number | undefined =>
    element.getOutlineWidth?.() || element.getStrokeWidth?.() || element.strokeWidth

watch(
    () => props.selectedGroup,
    (group) => {
        const objects = group?.getObjects();
        if (!objects?.length) return;

        const firstElement = objects[0];
        resolveType(firstElement as TextElement);

        // Warped text keeps its stroke under `stroke*` keys, every other type under `outline*`;
        //  the write path already routes through this map, so the reads below must too
        const { strokeOption, strokeColorsMap, strokeLibraryId } = outlineKeyMap.value;

        // A new selection recomputes these from scratch; the loop below only ever raises them
        model.isColorLibraryIdMixed = false;
        model.isFontColorsMixed = false;
        model.isOutlineColorsMixed = false;
        model.isOutlineColorLibraryIdMixed = false;
        disabled.value = firstElement.locked;

        // Seed before comparing, so two legacy curved texts with equal colors don't read as mixed
        if (type.value.circularText) {
            for (const element of objects) seedCircularOutlineColor(element as TextElement);
        }

        model.fontColorOption = firstElement.fontColorOption || 1;
        model.outlineColorOption = firstElement[strokeOption] || 1;
        model.outlineWidth = getElementStroke(firstElement);
        for (const element of objects) {
            if (model.isColorLibraryIdMixed &&
                model.isFontColorsMixed &&
                model.isOutlineColorsMixed &&
                model.isOutlineColorLibraryIdMixed
            ) break;

            if (element.colorLibraryId !== firstElement.colorLibraryId) {
                model.isColorLibraryIdMixed = true;
            }

            if (element[strokeLibraryId] !== firstElement[strokeLibraryId]) {
                model.isOutlineColorLibraryIdMixed = true;
            }

            if (!model.isFontColorsMixed) {
                model.isFontColorsMixed = isColorMapMixed(firstElement.fontColorsMap, element.fontColorsMap);
            }

            if (!model.isOutlineColorsMixed) {
                model.isOutlineColorsMixed = isColorMapMixed(firstElement[strokeColorsMap], element[strokeColorsMap]);
            }

            if (model.fontColorOption !== element.fontColorOption) {
                model.fontColorOption = undefined;
            }

            if (model.outlineColorOption !== element[strokeOption]) {
                model.outlineColorOption = undefined;
            }

            if (model.outlineWidth !== getElementStroke(element)) {
                model.outlineWidth = undefined;
            }
        }

        // Initialize color properties
        if (!model.isColorLibraryIdMixed) model.colorLibraryId = firstElement.colorLibraryId;
        if (!model.isFontColorsMixed) model.fontColorsMap = new Map(firstElement.fontColorsMap);

        // Initialize outline color properties
        isStrokeEnabled.value = !!model.outlineWidth || model.isOutlineColorLibraryIdMixed || model.isOutlineColorsMixed;
        if (!model.isOutlineColorLibraryIdMixed) model.outlineColorLibraryId = firstElement[strokeLibraryId];
        if (!model.isOutlineColorsMixed) model.outlineColorsMap = new Map(firstElement[strokeColorsMap]);
    },
    { immediate: true }
);

const updateElement = async (
    callback: (element: BaseTextElement, list?: BaseTextElement[]) => void | Promise<void>
) => {
    const promises: Promise<void>[] = [];
    const objects = props.selectedGroup ? props.selectedGroup.getObjects() : [props.textElement as BaseTextElement];

    for (const element of objects) {
        if (!element) continue;
        promises.push(Promise.resolve(callback(element, objects)));
    }
    await runTasksInChunks(promises);
    const uuid = (props.textElement as BaseTextElement)?.uuid;
    if (uuid) emit('element-props-updated', uuid);
};

const onColorSelected = async (value: ElementMapOption, option = 1) => {
    const color = value as FontColorOption;
    await updateElement((element) => {
        if (props.editMode) {
            element.setEditedProp('fontColorsMap', 'color');
        }

        if (model.isFontColorsMixed) element.fontColorsMap.clear();

        element.fontColorsMap.set(option, color);
        model.fontColorsMap = new Map(element.fontColorsMap);

        if (option == 1) {
            if (typeof color !== 'string' && 'colorStops' in color) {
                element.color = { ...color.colorStops[0] };
            } else if (typeof color !== 'string' && 'colors' in color) {
                element.color = { ...color.colors[0] };
            } else {
                element.color = { ...color };
            }
        }
        loadColorIntoElement(option, undefined, false, element);
    });
    model.isFontColorsMixed = false;
};

const onOutlineColorSelected = async (value: ElementMapOption, option = 1, clearOptions = false) => {
    const color = value as FontColorOption;
    const { strokeColorsMap, strokeColor } = outlineKeyMap.value;
    await updateElement((element) => {
        if (props.editMode) {
            element.setEditedProp(strokeColorsMap, strokeColor);
        }
        if (model.isOutlineColorsMixed || clearOptions) element[strokeColorsMap].clear();

        element[strokeColorsMap].set(option, color);
        model.outlineColorsMap = new Map(element[strokeColorsMap]);
        if (option == 1) {
            if (typeof color !== 'string' && 'colorStops' in color) {
                element[strokeColor] = color.colorStops[0];
            } else if (typeof color !== 'string' && 'colors' in color) {
                element[strokeColor] = color.colors[0];
            } else {
                element[strokeColor] = color;
            }
        }

        loadColorIntoElement(option, undefined, true, element);
    });
    model.isOutlineColorsMixed = false;
};

const outlineWidthPlaceholder = computed(() => props.selectedGroup ? 'Mixed' : '0')
const updateOutlineWidth = (value: number | string) => {
    const key = type.value.circularText || type.value.warpText ? 'strokeWidth' : 'outlineWidth';
    model.outlineWidth = Number(value);
    updateElement((element) => {
        element.set(key, model.outlineWidth ?? 0);
        if (props.editMode) {
            element.setEditedProp(key);
        }
    })
    props.canvas.requestRenderAll();
};

const cachedLibElements = new Map<string, LibraryDetailElement[]>();
const onLoadOption = (option: number, value?: ElementMapOption, isOutline?: boolean) => {
    updateElement(async (element, list) => {
        let val = value;
        if (!list || list.length < 2) {
            loadColorIntoElement(option, val, isOutline, element);
        }

        const libraryId = isOutline ? element[outlineKeyMap.value.strokeLibraryId] : element.colorLibraryId;
        if (libraryId) {
            const id = `${libraryId}-${option}`;
            const libElement =
                cachedLibElements.get(id) ||
                (await getLibraryElementDetails(libraryId, COLOR_TYPE, undefined, '', option));
            if (!libElement.length) return;
            cachedLibElements.set(id, libElement);
            val = {
              hex: libElement[0].hex || '',
              alpha: libElement[0].alpha || 1,
              cmyk: ''
            };
        }

        return loadColorIntoElement(option, val, isOutline, element);
    });
};

const previewColorOption = (color?: ElementMapOption, isOutline?: boolean) => {
    updateElement((element) => {
        loadColorIntoElement(model.fontColorOption || 1, color, isOutline, element);
    });
};

const loadColorIntoElement = (option: number, value?: ElementMapOption, isOutline?: boolean, el?: BaseTextElement) => {
    const element = el || props.textElement as BaseTextElement;
    if (!element) return;

    const { strokeColorsMap, strokeOption } = outlineKeyMap.value;
    let colorMap;
    if (isOutline) {
        colorMap = element[strokeColorsMap];
    } else {
        colorMap = element.fontColorsMap;
    }
    const color = (value as FontColorOption) || colorMap.get(option);

    if (!color) return;

    let objColor: string | GradientObject | PaletteObject;
    if ('colorStops' in color || 'colors' in color) {
        objColor = color;
    } else {
        objColor = color.alpha < 1 ? colord.hexToRgba(color.hex, color.alpha) : color.hex;
    }

    if (isOutline) {
        type.value.warpText ? element.setStrokeColor(objColor) : element.setOutlineColor(objColor);
        element[strokeOption] = model.outlineColorOption = option;
    } else {
        element.setColor(objColor);
        element.fontColorOption = model.fontColorOption = option;
    }

    props.canvas.requestRenderAll();
};

const removeColorOptionFromMap = (position: number, key: COLOR_MAP = 'fontColorsMap') => {
    const isOutline = key != 'fontColorsMap';
    const modelKey = isOutline ? 'outlineColorsMap' : key;
    updateElement((element) => {
        if (props.editMode) {
            const type = isOutline ? outlineKeyMap.value.type : 'color';
            element.setEditedProp(key, type);
        }
        element[key].delete(position);
        model[modelKey].delete(position);
        loadColorIntoElement(1, undefined, isOutline, element);
    });
    model[modelKey] = new Map(model[modelKey]);
};

const onColorOptionsReordered = (
    options: KeyValueDto<number, ElementMapOption>[],
    key: COLOR_MAP = 'fontColorsMap'
) => {
    const colorMap = new Map<number, FontColorOption>();
    const isOutline = key != 'fontColorsMap';
    const modelKey = isOutline ? 'outlineColorsMap' : key;
    const type = isOutline ? outlineKeyMap.value.type : 'color';
    options.forEach(({ value }, index) => {
        const position = index + 1;
        colorMap.set(position, value as FontColorOption);
    });
    updateElement((element) => {
        if (props.editMode) {
            element.setEditedProp(key, type);
        }

        element[key] = colorMap;
        model[modelKey] = new Map(colorMap);
        loadColorIntoElement(1, undefined, isOutline, element);
    });
};

const pickColorLibrary = (outline?: boolean) => {
    const optionKey = outline ? outlineKeyMap.value.strokeOption : 'fontColorOption';
    updateElement((el) => {
        el[optionKey] = model.outlineColorOption = 1;
    });

    if (!outline) {
        model.isColorLibraryIdMixed = false;
        emit('open-color-library-selector');
        return;
    }

    if (type.value.warpText) {
        emit('open-stroke-color-library-selector');
        return;
    }

    emit('open-outline-color-library-selector');
    model.isOutlineColorLibraryIdMixed = false;
};

const removeColorLibrary = (key: LIBRARY_MAP = 'colorLibraryId') => {
    const isOutline = key != 'colorLibraryId';
    updateElement((element) => {
        element[key] = undefined;
        if (props.editMode) {
            element.setEditedProp(key);
        }
        loadColorIntoElement(1, undefined, isOutline, element);
    });

    if (isOutline) {
        model.isOutlineColorLibraryIdMixed = false;
        model.outlineColorLibraryId = undefined;
        return;
    }

    model.isColorLibraryIdMixed = false;
    model.colorLibraryId = undefined;
};

const userTextures = computed(() =>
     props.textureList.map<CSelectItem<string, UserTextTexture>>(texture => ({
        label: texture.Name.replace(/\.[^/.]+$/, ''),
        value: texture.Path,
        deletable: props.allowTextureUpload && !!texture.UserId
    }))
);

const onTextureSelected = (item?: CSelectItem<Primitive> | Primitive) => {
    emit('texture-selected', item as string)
};

const onTextureUpload = (file: File[]) => {
    emit('texture-upload', file[0])
};

const deleteFontTexture = (texturePath: Primitive) => {
    emit('texture-delete', texturePath as string);
}

const changeTextureScale = () => {
    const element = props.textElement as BoundedTextElement;
    if (!element) return;

    element.set('textureScale', model.textureScale);
    const texture = element.textures.find(t => t.texturePath === element.texture);
    if (texture) {
        element.set('textures', [{
            ...texture,
            textureScale: model.textureScale
        }]);
    }
    if (props.editMode) {
        element.setEditedProp('textureScale');
        element.setEditedProp('textures');
    }
    props.canvas.requestRenderAll();
};

const toggleStrokeSection = () => {
    let width = 10;
    let color = INITIAL_STROKE_COLOR;
    isStrokeEnabled.value = !isStrokeEnabled.value;

    if (!isStrokeEnabled.value) {
        width = 0;
        color = DEFAULT_STROKE_COLOR;
    }

    const colorValue = transformHexToColorObj(color);
    onOutlineColorSelected(colorValue, 1, true);
    updateOutlineWidth(width);
};

const currentSlot = computed(() => (!model.isOutlineColorsMixed || model.isOutlineColorLibraryIdMixed ? 'header' : 'actions'));
const isHeaderSlotActive = computed(() => currentSlot.value === 'header');

/* Copy/paste color options across Fill, Stroke, Shadow and Vector single-color sections */
const clipboard = useColorOptionsClipboard();
const toast = useToaster();

const pastableClip = computed(() => clipboard.clip());
const canPasteFillColors = computed(() => !disabled.value && !!pastableClip.value);
// Stroke has no gradient/palette picker, so a Fill clip carrying one can't land here
const canPasteStrokeColors = computed(() => !disabled.value && !!pastableClip.value && !pastableClip.value?.hasNonSolidColor);

const onCopyFillColors = () => {
    clipboard.copy({
        colors: new Map(model.fontColorsMap),
        libraryId: model.colorLibraryId
    });
    fireDatadogLog(DatadogEvent.CopyDsColorOptions);
    toast.show('Fill colors copied');
};

const onPasteFillColors = async () => {
    const clip = clipboard.clip();
    if (!clip) return;
    await updateElement((element) => {
        if (props.editMode) {
            element.setEditedProp('fontColorsMap', 'color', 'colorLibraryId');
        }
        const colorMap = new Map(clip.colors as Map<number, FontColorOption>);
        element.fontColorsMap = colorMap;
        element.colorLibraryId = clip.libraryId;
        // Set explicitly instead of relying on loadColorIntoElement's fallback: a clip copied from a
        //  purely library-linked Fill (no manual colors) has nothing at position 1 to fall back to.
        element.fontColorOption = model.fontColorOption = 1;
        model.fontColorsMap = new Map(colorMap);
        model.colorLibraryId = clip.libraryId;
        model.isFontColorsMixed = false;
        model.isColorLibraryIdMixed = false;
        loadColorIntoElement(1, undefined, false, element);
    });
    fireDatadogLog(DatadogEvent.PasteDsColorOptions);
    recordFeatureUsage(TrackedFeature.ColorsPasteFill);
    toast.show('Fill colors pasted');
};

const onCopyStrokeColors = () => {
    clipboard.copy({
        colors: new Map(model.outlineColorsMap),
        libraryId: model.outlineColorLibraryId
    });
    fireDatadogLog(DatadogEvent.CopyDsColorOptions);
    toast.show('Stroke colors copied');
};

const onPasteStrokeColors = async () => {
    const clip = clipboard.clip();
    if (!clip || clip.hasNonSolidColor) return;
    const { strokeColorsMap, strokeLibraryId, strokeColor, strokeOption } = outlineKeyMap.value;
    const wasStrokeEnabled = isStrokeEnabled.value;
    await updateElement((element) => {
        if (props.editMode) {
            element.setEditedProp(strokeColorsMap, strokeColor, strokeLibraryId);
        }
        const colorMap = new Map(clip.colors as Map<number, FontColorOption>);
        element[strokeColorsMap] = colorMap;
        element[strokeLibraryId] = clip.libraryId;
        // Set explicitly instead of relying on loadColorIntoElement's fallback: a clip copied from a
        //  purely library-linked Stroke (no manual colors) has nothing at position 1 to fall back to.
        element[strokeOption] = model.outlineColorOption = 1;
        model.outlineColorsMap = new Map(colorMap);
        model.outlineColorLibraryId = clip.libraryId;
        model.isOutlineColorsMixed = false;
        model.isOutlineColorLibraryIdMixed = false;
        loadColorIntoElement(1, undefined, true, element);
    });
    isStrokeEnabled.value = true;
    // Pasting into a text with no stroke yet would otherwise be invisible: 0 width same as no stroke
    if (!wasStrokeEnabled) {
        updateOutlineWidth(10);
    }
    fireDatadogLog(DatadogEvent.PasteDsColorOptions);
    recordFeatureUsage(TrackedFeature.ColorsPasteStroke);
    toast.show('Stroke colors pasted');
};
</script>

<template>
    <div class="text-color-wrapper">
        <section class="color-section">
            <color-options-clipboard-buttons
                title="Fill"
                :can-copy="!disabled"
                :can-paste="canPasteFillColors"
                @copy="onCopyFillColors"
                @paste="onPasteFillColors"
            />
            <div class="border-bottom" />
            <element-library-selector
                :disabled="disabled"
                :element-list="model.fontColorsMap"
                :library-id="model.colorLibraryId"
                :selected-group="selectedGroup"
                :library-type="COLOR_TYPE"
                :selected-position="model.fontColorOption"
                :cmyk="cmykMode"
                :show-menu-types="showMenuTypes"
                :enable-palette="isPaletteEnabled"
                :mixed-library="model.isColorLibraryIdMixed"
                :mixed-element-list="model.isFontColorsMixed"
                @pick-library="pickColorLibrary"
                @remove-library="removeColorLibrary"
                @option-selected="onColorSelected"
                @remove-map-option="removeColorOptionFromMap"
                @load-option="onLoadOption"
                @options-reordered="onColorOptionsReordered"
                @preview-selection="previewColorOption"
            />
        </section>
        <section class="color-section">
            <color-options-clipboard-buttons
                title="Stroke"
                :can-copy="!disabled && isStrokeEnabled"
                :can-paste="canPasteStrokeColors"
                @copy="onCopyStrokeColors"
                @paste="onPasteStrokeColors"
            />
            <div class="border-bottom" />
            <c-button
                v-if="!isStrokeEnabled"
                variant="secondary"
                text="Add Stroke"
                :icon="['far', 'plus']"
                :disabled="disabled"
                @click="toggleStrokeSection"
            />
            <element-library-selector
                v-else
                :disabled="disabled"
                :element-list="model.outlineColorsMap"
                :library-id="model.outlineColorLibraryId"
                :library-type="COLOR_TYPE"
                :selected-position="model.outlineColorOption"
                :cmyk="cmykMode"
                :mixed-library="model.isOutlineColorLibraryIdMixed"
                :mixed-element-list="model.isOutlineColorsMixed"
                @pick-library="pickColorLibrary(true)"
                @remove-library="removeColorLibrary(outlineKeyMap.strokeLibraryId)"
                @option-selected="onOutlineColorSelected"
                @remove-map-option="removeColorOptionFromMap($event, outlineKeyMap.strokeColorsMap)"
                @load-option="(option, value) => onLoadOption(option, value, true)"
                @options-reordered="onColorOptionsReordered($event, outlineKeyMap.strokeColorsMap)"
                @preview-selection="previewColorOption($event, true)"
            >
                <template :slot="currentSlot">
                    <c-input
                        v-model="model.outlineWidth"
                        label="Width"
                        :min="0"
                        :placeholder="outlineWidthPlaceholder"
                        type="number"
                        :disabled="disabled"
                        @input="updateOutlineWidth"
                    />
                </template>
                <template v-if="isHeaderSlotActive" #actions>
                    <c-button
                        v-if="isStrokeEnabled"
                        class="remove-stroke-btn"
                        variant="secondary"
                        text="Remove Stroke"
                        :icon="['far', 'trash']"
                        :disabled="disabled"
                        @click="toggleStrokeSection"
                    />
                </template>
            </element-library-selector>
        </section>
        <section v-if="type.text && !props.selectedGroup && !hasTextPathDraw" class="color-section">
            <h6>Texture</h6>
            <div class="border-bottom" />
            <div class="row align-items-center">
                <div class="col-6 d-flex align-items-end pr-0">
                    <c-select
                        class="flex-fill"
                        label="Texture"
                        placeholder="Select a texture"
                        clearable
                        searchable
                        filterable
                        :value="model.texture"
                        :disabled="disabled || isTextureDisabled || shadowApplied"
                        :options="userTextures"
                        :model-transform="valueReducer"
                        @input="onTextureSelected"
                        @delete-option="deleteFontTexture($event.value)"
                    />
                    <c-file-input
                        v-if="allowTextureUpload"
                        :value="[]"
                        accept=".png,.jpg,.jpeg"
                        :display-preview="false"
                        :disabled="disabled || isTextureDisabled || shadowApplied"
                        @input="onTextureUpload"
                    >
                        <template #uploadTrigger="{ onFileUpload, disabled: triggerDisabled }">
                            <c-button
                                class="ml-2"
                                variant="secondary"
                                :icon="['far', 'plus']"
                                :disabled="triggerDisabled"
                                @click="onFileUpload"
                            />
                        </template>
                    </c-file-input>
                </div>
                <div class="col-6">
                    <label>Scale: {{ model.textureScale }}%</label>
                    <c-slider
                        v-model="model.textureScale"
                        formatter="{value}%"
                        :max="500"
                        :disabled="disabled || isTextureDisabled || shadowApplied || !model.textures.length"
                        @input="changeTextureScale"
                    />
                </div>
            </div>
        </section>
    </div>
</template>

<style lang="scss" scoped>
.text-color-wrapper {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    .color-section {
        display: flex;
        flex-direction: column;
        gap: 0.625rem;
        h6 {
            font-size: 0.875rem;
            margin-bottom: 0;
        }
    }
    .element-library-selector-wrapper {
        .remove-stroke-btn {
            flex: 1 1 auto;
            order: 4;
        }
    }
}
</style>
